import React, { useState, useEffect } from 'react';
import { Landmark, CheckCircle, XCircle, Clock, Loader2 } from 'lucide-react';
import { adminAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const STATUS_BADGE = {
  pending: { label: 'En attente', cls: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Effectue', cls: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Refuse', cls: 'bg-red-100 text-red-700' },
};

export default function AdminWithdrawalsTab() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getWithdrawals(filter);
      setWithdrawals(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const handleProcess = async (id, action) => {
    const note = action === 'rejected' ? window.prompt('Raison du refus (optionnel) :') || '' : '';
    setProcessing(id);
    try {
      await adminAPI.processWithdrawal(id, { action, note });
      toast.success(action === 'completed' ? 'Retrait valide' : 'Retrait refuse');
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setProcessing(null); }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        {['pending', 'completed', 'rejected', 'all'].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === s ? 'bg-[#0C63BF] text-white' : 'bg-[#E8F4FD] text-[#5A8BA8] hover:bg-[#D6E8F0]'}`}
            data-testid={`filter-${s}`}>
            {s === 'pending' ? 'En attente' : s === 'completed' ? 'Effectues' : s === 'rejected' ? 'Refuses' : 'Tous'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-[#E8F4FD] rounded-xl animate-pulse" />)}</div>
      ) : withdrawals.length === 0 ? (
        <div className="text-center py-12">
          <Landmark className="w-10 h-10 text-[#B8D4E3] mx-auto mb-3" />
          <p className="text-[#5A8BA8] text-sm">Aucune demande de retrait</p>
        </div>
      ) : (
        <div className="space-y-3">
          {withdrawals.map(w => {
            const badge = STATUS_BADGE[w.status] || STATUS_BADGE.pending;
            return (
              <div key={w.id} className="bg-white rounded-xl border border-[#D6E8F0] p-4" data-testid={`withdrawal-${w.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-bold text-[#033871]">{w.user_name}</p>
                    <p className="text-xs text-[#5A8BA8]">{w.user_email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-black text-[#0C63BF]">{w.amount.toFixed(2)}&euro;</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>{badge.label}</span>
                  </div>
                </div>
                <div className="text-xs text-[#5A8BA8] space-y-1 mb-3">
                  <p>IBAN : <span className="font-mono">{w.bank_details?.iban?.slice(0, 4)} **** {w.bank_details?.iban?.slice(-4)}</span></p>
                  {w.bank_details?.bic && <p>BIC : <span className="font-mono">{w.bank_details.bic}</span></p>}
                  <p>Demande le : {new Date(w.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  {w.admin_note && <p className="text-amber-600">Note : {w.admin_note}</p>}
                </div>
                {w.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleProcess(w.id, 'completed')} disabled={processing === w.id}
                      className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid={`approve-${w.id}`}>
                      {processing === w.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                      Valider le virement
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleProcess(w.id, 'rejected')} disabled={processing === w.id}
                      className="text-red-500 border-red-200 hover:bg-red-50 gap-1" data-testid={`reject-${w.id}`}>
                      <XCircle className="w-3 h-3" /> Refuser
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
